import React, { useState } from 'react'
import { Box, IconButton, Link, MenuItem, Paper, Select, TableCell, TableRow, TextField, Typography } from '@mui/material'
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis,
} from 'recharts'
import LinkedInIcon from '@mui/icons-material/LinkedIn'
import BoltIcon from '@mui/icons-material/Bolt'
import SendIcon from '@mui/icons-material/Send'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import ForumIcon from '@mui/icons-material/Forum'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfiedAlt'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import CheckIcon from '@mui/icons-material/Check'
import { useGetLinkedInAbmQuery, useUpdateLinkedInAbmWorkflowMutation } from '../../redux/growthosLinkedinAbmApiSlice'
import { useListIngestRunsQuery } from '../../redux/growthosConnectorsApiSlice'
import {
  GX_BORDER, GX_INK_500, GX_INK_700, GX_INK_900, GX_CARD_SX, GX_COLORS, GX_PAGE_SX, GX_POSITIVE,
  GxKpiCard, GxSectionCard, GxFunnelStage, GxPill, GxSyncStatusBar,
} from '../growthos-shared/GrowthOsUi'
import { GxCollapsePanel, GxPagedTable, gxBodyCellSx, gxInt } from '../growthos-shared/GxPagedTable'
import GxRequestCampaignSection from '../growthos-shared/GxRequestCampaignSection'

/* Port of the reference app/(dashboard)/analytics/linkedin-abm/page.tsx, on lemlist data:
   Programme Activity KPIs (clickable -> workflow / reply drill), Outreach Outcomes tiles,
   Outreach Funnel (every stage clickable), Reply Sentiment donut + By Target Category bars
   (slices/bars clickable), Leaderboard by Sales Leader (TOTAL row; a leader row expands into that
   leader's workflows + replies), Request a Campaign. */

const PERIODS = [
  { value: 'mtd', label: 'MTD' }, { value: 'last_month', label: 'Last Month' },
  { value: 'last_3m', label: 'Last 3M' }, { value: 'last_6m', label: 'Last 6M' },
  { value: 'ytd', label: 'YTD' }, { value: 'all', label: 'All time' }, { value: 'custom', label: 'Custom' },
]
const SYNC_LABELS = { linkedin_abm_lemlist: 'LinkedIn/ABM (lemlist)' }
const DONUT_COLORS = ['#4F46E5', '#06B6D4', '#0D9488', '#D97706', '#E11D48', '#7C3AED']
const FUNNEL_META = {
  connectionsSent: { icon: <SendIcon sx={{ fontSize: 18 }} />, gradient: 'linear-gradient(135deg,#4F46E5,#06B6D4)' },
  connectionsAccepted: { icon: <PersonAddIcon sx={{ fontSize: 18 }} />, gradient: 'linear-gradient(135deg,#0D9488,#34D399)' },
  repliesReceived: { icon: <ForumIcon sx={{ fontSize: 18 }} />, gradient: 'linear-gradient(135deg,#7C3AED,#C084FC)' },
}
const funnelWidthPct = (pct, floor = 30) => floor + (100 - floor) * Math.sqrt(Math.max(0, Math.min(100, pct ?? 0)) / 100)

const fmtRate = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`)
const shortDate = (iso) => (iso
  ? new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
  : '—')
const comparison = (cur, prev) => {
  if (prev == null) return undefined
  const ch = prev > 0 ? Math.round(((cur - prev) / prev) * 1000) / 10 : null
  return `Previous: ${gxInt(prev)}${ch == null ? '' : ` · ${ch >= 0 ? '↑' : '↓'} ${Math.abs(ch).toFixed(1)}%`}`
}
const hubRecordUrl = (hub, id) => (hub && id ? `https://${hub.uiDomain}/contacts/${hub.portalId}/record/0-2/${id}` : undefined)
// lemlist replies carry no HubSpot contact id — deep-link to HubSpot's contact search (email, else name).
const hubContactSearchUrl = (hub, q) => (hub && q ? `https://${hub.uiDomain}/contacts/${hub.portalId}/objects/0-1/views/all/list?query=${encodeURIComponent(q)}` : undefined)

function CategoryCell({ w }) {
  const [updateWorkflow] = useUpdateLinkedInAbmWorkflowMutation()
  const [draft, setDraft] = useState(undefined)
  const current = w.category === 'Uncategorized' || w.category === 'Not set' ? '' : w.category
  const dirty = draft !== undefined && draft !== current
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
      <TextField size="small" variant="standard" placeholder="Uncategorized" value={draft ?? current}
        onChange={(e) => setDraft(e.target.value)} sx={{ width: 130 }} inputProps={{ style: { fontSize: 12 } }}
        title="Target category — lemlist has no category field; set it here (never overwritten by sync)." />
      {dirty && (
        <IconButton size="small" aria-label="Save category"
          onClick={() => { updateWorkflow({ campaignId: w.campaignId, body: { category: draft } }); setDraft(undefined) }}>
          <CheckIcon fontSize="inherit" />
        </IconButton>
      )}
    </Box>
  )
}

function WorkflowsTable({ rows }) {
  return (
    <GxPagedTable
      rows={rows}
      rowKey={(w) => w.campaignId}
      filter={(w, q) => `${w.campaignId} ${w.name || ''} ${w.salesLeader} ${w.category}`.toLowerCase().includes(q)}
      filterPlaceholder="Filter workflows…"
      emptyText="No workflows match this filter."
      columns={[
        { key: 'workflow', header: 'Workflow', title: 'lemlist campaign name and id.', sortValue: (w) => w.name || '',
          cell: (w) => <><Typography sx={{ fontSize: 12, fontWeight: 600, color: GX_INK_900 }}>{w.name || w.campaignId}</Typography><Typography sx={{ fontSize: 11, color: GX_INK_500 }}>{w.campaignId}</Typography></> },
        { key: 'leader', header: 'Sales POC', title: 'The campaign’s LinkedIn sender.', sortValue: (w) => w.salesLeader, cell: (w) => w.salesLeader },
        { key: 'category', header: 'Category', title: 'Target category (admin-maintained).', sortValue: (w) => w.category, cell: (w) => <CategoryCell w={w} /> },
        { key: 'created', header: 'Created', title: 'Campaign create date (lemlist).', sortValue: (w) => w.dateCreated || '', cell: (w) => shortDate(w.dateCreated) },
        { key: 'sent', header: 'Sent', title: 'Connections sent (campaign tally).', align: 'right', sortValue: (w) => w.connectionsSent, cell: (w) => gxInt(w.connectionsSent) },
        { key: 'accepted', header: 'Accepted', title: 'Connections accepted (campaign tally).', align: 'right', sortValue: (w) => w.connectionsAccepted, cell: (w) => gxInt(w.connectionsAccepted) },
        { key: 'acceptPct', header: 'Acceptance %', title: 'Connections accepted ÷ sent × 100.', align: 'right', sortValue: (w) => w.acceptancePct ?? -1, cell: (w) => fmtRate(w.acceptancePct) },
        { key: 'followups', header: 'Follow-ups', title: 'Follow-up messages sent (campaign tally).', align: 'right', sortValue: (w) => w.followUpsSent, cell: (w) => gxInt(w.followUpsSent) },
        { key: 'replies', header: 'Replies', title: 'LinkedIn replies (lemlist campaign counter).', align: 'right', sortValue: (w) => w.repliesReceived, cell: (w) => gxInt(w.repliesReceived) },
        { key: 'replyPct', header: 'Reply %', title: 'Replies ÷ connections accepted × 100.', align: 'right', sortValue: (w) => w.replyPct ?? -1, cell: (w) => fmtRate(w.replyPct) },
        { key: 'meetings', header: 'Meetings/SQLs', title: 'Meetings booked (lemlist).', align: 'right', sortValue: (w) => w.meetingsBooked, cell: (w) => gxInt(w.meetingsBooked) },
        { key: 'status', header: 'Status', title: 'lemlist campaign status (running = active).', sortValue: (w) => w.status || '',
          cell: (w) => (
            <Box component="span" sx={{
              display: 'inline-block', px: 1, py: 0.25, borderRadius: '9999px', fontSize: 11, fontWeight: 600,
              border: `1px solid ${w.active ? 'rgba(5,150,105,0.3)' : GX_BORDER}`, color: w.active ? GX_POSITIVE : GX_INK_500,
            }}>{w.status || '—'}</Box>
          ) },
      ]}
    />
  )
}

// Reply message — clamped to 2 lines with an inline "See more"/"See less".
function MessageBody({ text }) {
  const [open, setOpen] = useState(false)
  if (!text) return <>—</>
  const long = text.length > 90
  return (
    <>
      <Box component="span" sx={{
        whiteSpace: 'normal', wordBreak: 'break-word',
        ...(open || !long ? {} : { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }),
      }}>{text}</Box>
      {long && (
        <Box component="button" onClick={() => setOpen((o) => !o)}
          sx={{ ml: 0.5, border: 'none', background: 'none', p: 0, cursor: 'pointer', fontSize: 11, fontWeight: 600, color: GX_COLORS.indigo, whiteSpace: 'nowrap' }}>
          {open ? 'See less' : 'See more'}
        </Box>
      )}
    </>
  )
}

function RepliesTable({ rows, hub }) {
  return (
    <GxPagedTable
      rows={rows}
      rowKey={(r, i) => r.replyId || i}
      filter={(r, q) => `${r.contactName || ''} ${r.company || ''} ${r.leader} ${r.campaignName || ''} ${r.sentiment}`.toLowerCase().includes(q)}
      filterPlaceholder="Filter replies…"
      emptyText="No replies match this filter."
      columns={[
        { key: 'date', header: 'Date', title: 'Reply date (first LinkedIn reply in the conversation).', sortValue: (r) => r.replyDate || '', cell: (r) => shortDate(r.replyDate) },
        { key: 'workflow', header: 'Workflow', title: 'lemlist campaign.', sortValue: (r) => r.campaignName || r.workflowId || '',
          cell: (r) => (
            <>
              {r.campaignName || r.workflowId || '—'}
              {r.workflowUnresolved && <Box component="span" title="Campaign not found among synced workflows" sx={{ ml: 0.5, color: '#e11d48' }}>⚠</Box>}
            </>
          ) },
        { key: 'leader', header: 'Sales POC', title: 'The campaign’s LinkedIn sender.', sortValue: (r) => r.leader, cell: (r) => r.leader },
        { key: 'contact', header: 'Contact', title: 'Lead name. Click to open in HubSpot (searches by email).', sortValue: (r) => r.contactName || '',
          cell: (r) => {
            const href = hubContactSearchUrl(hub, r.contactEmail || r.contactName)
            const name = r.contactName || '—'
            return (
              <>
                {href ? <Link href={href} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ fontSize: 12, fontWeight: 600 }}>{name}</Link>
                  : <Typography component="span" sx={{ fontSize: 12, fontWeight: 600 }}>{name}</Typography>}
                {r.contactEmail && <Typography sx={{ fontSize: 11, color: GX_INK_500 }}>{r.contactEmail}</Typography>}
              </>
            )
          } },
        { key: 'company', header: 'Company', title: 'HubSpot company matched by the lead’s email domain.', sortValue: (r) => r.company || '',
          cell: (r) => {
            const href = hubRecordUrl(hub, r.hubspotCompanyId)
            if (!r.company) return '—'
            return href ? <Link href={href} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ fontSize: 12 }}>{r.company}</Link> : r.company
          } },
        { key: 'sentiment', header: 'Sentiment', title: 'From lemlist’s AI interest score (≥0.6 Positive, ≤0.3 Negative); Not set = no score.', sortValue: (r) => r.sentiment, cell: (r) => r.sentiment },
        { key: 'message', header: 'Message', title: 'The reply message text.', cellSx: { maxWidth: 340, minWidth: 220, whiteSpace: 'normal', verticalAlign: 'top' }, cell: (r) => <MessageBody text={r.messageReceived} /> },
      ]}
    />
  )
}

// Large headline tile (Conversations Logged, Meetings/SQLs) — bigger than a KPI card.
function OutcomeTile({ label, value, caption, icon, gradient, onClick, active }) {
  return (
    <Paper
      variant="outlined" onClick={onClick}
      sx={{
        ...GX_CARD_SX, position: 'relative', overflow: 'hidden', p: 3, flex: 1, minWidth: 300,
        cursor: onClick ? 'pointer' : 'default', transition: 'transform 200ms',
        '&:hover': onClick ? { transform: 'scale(1.01)' } : undefined,
        boxShadow: active ? `0 0 0 2px ${GX_COLORS.indigo}, ${GX_CARD_SX.boxShadow}` : GX_CARD_SX.boxShadow,
      }}
    >
      <Box sx={{ position: 'absolute', left: 0, right: 0, top: 0, height: 6, background: gradient }} />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.14em', color: GX_INK_500 }}>{label}</Typography>
        <Box sx={{ width: 40, height: 40, borderRadius: '12px', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', background: gradient }}>{icon}</Box>
      </Box>
      <Typography sx={{ mt: 2, fontSize: 48, fontWeight: 800, letterSpacing: '-0.02em', color: GX_INK_900, lineHeight: 1 }}>{value}</Typography>
      <Typography sx={{ mt: 1, fontSize: 12, color: GX_INK_500, lineHeight: 1.6 }}>{caption}</Typography>
    </Paper>
  )
}

function DrillPanel({ drill, workflows, replies, onClose, hub }) {
  if (!drill) return null
  const rows = drill.kind === 'workflows' ? workflows.filter(drill.predicate) : replies.filter(drill.predicate)
  return (
    <Paper variant="outlined" sx={{ ...GX_CARD_SX, mb: 3, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, pt: 2.5, pb: 1 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: GX_INK_700 }}>{drill.label}</Typography>
        <Box component="button" onClick={onClose} sx={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: GX_INK_500 }}>Close ✕</Box>
      </Box>
      <Box sx={{ px: 1.5, pb: 1.5 }}>
        {drill.kind === 'workflows' ? <WorkflowsTable rows={rows} /> : <RepliesTable rows={rows} hub={hub} />}
      </Box>
    </Paper>
  )
}

function LeaderDetail({ leader, workflows, replies, hub }) {
  const w = workflows.filter((x) => x.salesLeader === leader)
  const r = replies.filter((x) => x.leader === leader)
  const heading = { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: GX_INK_500, mb: 1 }
  return (
    <Box>
      <Typography sx={heading}>Workflows</Typography>
      <WorkflowsTable rows={w} />
      <Typography sx={{ ...heading, mt: 2 }}>Replies</Typography>
      <RepliesTable rows={r} hub={hub} />
    </Box>
  )
}

function LeaderboardTable({ rows, total, workflows, replies, hub }) {
  const [openLeader, setOpenLeader] = useState(null)
  const meetingPct = (r) => (r.replies > 0 ? (r.meetingsSqls / r.replies) * 100 : null)
  const footCell = { ...gxBodyCellSx, fontWeight: 700, color: GX_INK_900, bgcolor: '#f8fafc' }
  return (
    <GxCollapsePanel title="Leaderboard — by Sales Leader" count={rows.length} icon={<EmojiEventsIcon sx={{ fontSize: 16 }} />}>
      <Box sx={{ p: 1.5 }}>
        <GxPagedTable
          rows={rows}
          rowKey={(r) => r.salesLeader}
          filter={(r, q) => r.salesLeader.toLowerCase().includes(q)}
          filterPlaceholder="Filter by leader…"
          emptyText="No leaders match this filter."
          onRowClick={(r) => setOpenLeader((cur) => (cur === r.salesLeader ? null : r.salesLeader))}
          isRowActive={(r) => openLeader === r.salesLeader}
          renderExpanded={(r) => (
            <Box sx={{ p: 1.5, bgcolor: '#f8fafc' }}>
              <LeaderDetail leader={r.salesLeader} workflows={workflows} replies={replies} hub={hub} />
            </Box>
          )}
          columns={[
            { key: 'leader', header: 'Sales POC', title: 'The campaign’s LinkedIn sender (every known leader is listed).', sortValue: (r) => r.salesLeader,
              cell: (r) => (
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontWeight: 600, color: GX_INK_900 }}>
                  {r.salesLeader}
                  <ExpandMoreIcon sx={{ fontSize: 16, transition: 'transform 150ms', transform: openLeader === r.salesLeader ? 'rotate(180deg)' : 'none' }} />
                </Box>
              ) },
            { key: 'workflows', header: 'Workflows', title: 'Workflows created in the window.', align: 'right', sortValue: (r) => r.workflows, cell: (r) => gxInt(r.workflows) },
            { key: 'sent', header: 'Sent', title: 'Connections sent (campaign tally).', align: 'right', sortValue: (r) => r.connectionsSent, cell: (r) => gxInt(r.connectionsSent) },
            { key: 'accepted', header: 'Accepted', title: 'Connections accepted (campaign tally).', align: 'right', sortValue: (r) => r.connectionsAccepted, cell: (r) => gxInt(r.connectionsAccepted) },
            { key: 'acceptPct', header: 'Acceptance %', title: 'Connections accepted ÷ sent × 100.', align: 'right', sortValue: (r) => r.acceptancePct ?? -1, cell: (r) => fmtRate(r.acceptancePct) },
            { key: 'replies', header: 'Replies', title: 'Conversations (reply records) by reply date in the window.', align: 'right', sortValue: (r) => r.replies, cell: (r) => gxInt(r.replies) },
            { key: 'replyPct', header: 'Reply %', title: 'Replies ÷ connections accepted × 100.', align: 'right', sortValue: (r) => r.replyPct ?? -1, cell: (r) => fmtRate(r.replyPct) },
            { key: 'meetings', header: 'Meetings/SQLs', title: 'Meetings booked (lemlist).', align: 'right', sortValue: (r) => r.meetingsSqls, cell: (r) => gxInt(r.meetingsSqls) },
            { key: 'meetingPct', header: 'Meeting %', title: 'Meetings/SQLs ÷ Replies — who converts conversations into meetings.', align: 'right', sortValue: (r) => meetingPct(r) ?? -1, cell: (r) => fmtRate(meetingPct(r)) },
          ]}
          footer={total && (
            <TableRow>
              <TableCell sx={footCell} />
              <TableCell sx={footCell}>{total.salesLeader}</TableCell>
              <TableCell align="right" sx={footCell}>{gxInt(total.workflows)}</TableCell>
              <TableCell align="right" sx={footCell}>{gxInt(total.connectionsSent)}</TableCell>
              <TableCell align="right" sx={footCell}>{gxInt(total.connectionsAccepted)}</TableCell>
              <TableCell align="right" sx={footCell}>{fmtRate(total.acceptancePct)}</TableCell>
              <TableCell align="right" sx={footCell}>{gxInt(total.replies)}</TableCell>
              <TableCell align="right" sx={footCell}>{fmtRate(total.replyPct)}</TableCell>
              <TableCell align="right" sx={footCell}>{gxInt(total.meetingsSqls)}</TableCell>
              <TableCell align="right" sx={footCell}>{fmtRate(meetingPct(total))}</TableCell>
            </TableRow>
          )}
        />
      </Box>
    </GxCollapsePanel>
  )
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null
  return (
    <Box sx={{ border: `1px solid ${GX_BORDER}`, bgcolor: '#fff', borderRadius: '12px', p: 1.5, fontSize: 12, boxShadow: '0 8px 24px rgba(15,23,42,0.12)' }}>
      <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>{label || payload[0]?.name}</Typography>
      {payload.map((item) => (
        <Box key={item.dataKey || item.name} sx={{ display: 'flex', justifyContent: 'space-between', gap: 3 }}>
          <span>{item.name}</span><b>{gxInt(item.value)}</b>
        </Box>
      ))}
    </Box>
  )
}

export default function LinkedinAbmPage() {
  const [period, setPeriod] = useState('mtd')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [drill, setDrill] = useState(null)
  const customReady = period !== 'custom' || Boolean(customFrom && customTo)

  const { data, error, isFetching } = useGetLinkedInAbmQuery(
    { period, from: period === 'custom' ? customFrom : undefined, to: period === 'custom' ? customTo : undefined },
    { skip: !customReady },
  )
  const { data: runsData } = useListIngestRunsQuery()
  const workflows = data?.workflows || []
  const replies = data?.replies || []
  const prev = data?.previous
  const v = (x) => (data ? gxInt(x) : (isFetching ? '…' : '—'))

  // Re-clicking the open card/slice collapses it; a different one switches. Identity = kind + label.
  const openWorkflowsDrill = (label, predicate) =>
    setDrill((cur) => (cur?.kind === 'workflows' && cur.label === label ? null : { kind: 'workflows', label, predicate }))
  const openRepliesDrill = (label, predicate) =>
    setDrill((cur) => (cur?.kind === 'replies' && cur.label === label ? null : { kind: 'replies', label, predicate }))
  const isOpen = (kind, label) => drill?.kind === kind && drill.label === label

  const onStageClick = (key, label) => {
    // Sent/Accepted are campaign tallies -> contributing workflows; Replies is the row-level metric
    // -> the actual conversations (matches the stage count).
    if (key === 'repliesReceived') { openRepliesDrill('Replies Received', (r) => r.inWindow); return }
    const pred = key === 'connectionsAccepted'
      ? (w) => w.inWindow && w.connectionsAccepted > 0
      : (w) => w.inWindow && w.connectionsSent > 0
    openWorkflowsDrill(label, pred)
  }

  const funnelPct = (key) => (key === 'connectionsSent' ? data?.funnel?.sentPct
    : key === 'connectionsAccepted' ? data?.funnel?.acceptedPct : data?.funnel?.repliesPct)
  const sentiment = data?.sentimentBuckets || []
  const categoryChart = (data?.byCategory || []).map((c) => ({ name: c.category, sent: c.connectionsSent }))

  return (
    <Box sx={GX_PAGE_SX}>
      <GxSyncStatusBar runs={runsData?.runs || []} labels={SYNC_LABELS} />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 3 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
            <LinkedInIcon sx={{ fontSize: 14, color: GX_COLORS.indigo }} />
            <Typography sx={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.14em', color: GX_COLORS.indigo }}>LinkedIn / ABM Outreach · lemlist</Typography>
          </Box>
          <Typography sx={{ fontSize: 24, fontWeight: 700, color: GX_INK_900 }}>LinkedIn / ABM</Typography>
          <Typography sx={{ fontSize: 13, color: GX_INK_500, mt: 0.5, maxWidth: 760 }}>
            lemlist campaign &amp; reply tracker. Workflow activity counts campaigns created in the window;
            replies count conversations by reply date. Sync via Data → Connectors.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Select size="small" value={period} onChange={(e) => { setPeriod(e.target.value); setDrill(null) }} sx={{ minWidth: 144, fontSize: 13, bgcolor: '#fff' }}>
            {PERIODS.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
          </Select>
          {period === 'custom' && (
            <>
              <TextField size="small" type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
              <Typography sx={{ color: GX_INK_500 }}>→</Typography>
              <TextField size="small" type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
            </>
          )}
          {data?.window && <GxPill color={GX_COLORS.indigo}>Showing: {shortDate(data.window.from)} – {shortDate(data.window.to)}</GxPill>}
        </Box>
      </Box>

      {error && (
        <Box sx={{ border: '1px solid #fecdd3', bgcolor: '#fff1f2', color: '#be123c', borderRadius: '16px', p: 2, mb: 2, fontSize: 13 }}>
          Failed to load — {error?.data?.message || error?.message || `HTTP ${error?.status}`}
        </Box>
      )}
      {period === 'custom' && !customReady && (
        <Box sx={{ border: '1px solid #fde68a', bgcolor: '#fffbeb', color: '#b45309', borderRadius: '16px', p: 2, mb: 2, fontSize: 13 }}>Pick a start and end date to load the custom range.</Box>
      )}
      {data && data.hasData === false && (
        <Box sx={{ border: '1px solid #fde68a', bgcolor: '#fffbeb', color: '#b45309', borderRadius: '16px', p: 2, mb: 2, fontSize: 13 }}>No data yet — run a sync from Data → Connectors.</Box>
      )}

      {/* Programme Activity */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <LinkedInIcon sx={{ fontSize: 16, color: GX_COLORS.indigo }} />
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: GX_INK_700 }}>Programme Activity</Typography>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
        <GxKpiCard label="Workflows Logged" icon={<LinkedInIcon sx={{ fontSize: 16 }} />} iconColor={GX_COLORS.indigo}
          value={v(data?.workflowsLogged)} sub={data ? comparison(data.workflowsLogged, prev?.workflowsLogged) : undefined}
          onClick={() => openWorkflowsDrill('Workflows Logged', (w) => w.inWindow)} active={isOpen('workflows', 'Workflows Logged')} />
        <GxKpiCard label="Active Workflows" icon={<BoltIcon sx={{ fontSize: 16 }} />} iconColor={GX_COLORS.teal}
          value={v(data?.activeWorkflows)} sub="Currently Active (live)"
          onClick={() => openWorkflowsDrill('Active Workflows', (w) => w.active)} active={isOpen('workflows', 'Active Workflows')} />
        <GxKpiCard label="Connections Sent" icon={<SendIcon sx={{ fontSize: 16 }} />} iconColor={GX_COLORS.indigo}
          value={v(data?.connectionsSent)} sub={data ? comparison(data.connectionsSent, prev?.connectionsSent) : undefined}
          onClick={() => openWorkflowsDrill('Connections Sent', (w) => w.inWindow && w.connectionsSent > 0)} active={isOpen('workflows', 'Connections Sent')} />
        <GxKpiCard label="Connections Accepted" icon={<PersonAddIcon sx={{ fontSize: 16 }} />} iconColor={GX_COLORS.teal}
          value={v(data?.connectionsAccepted)} sub={data ? `Acceptance ${fmtRate(data.acceptancePct)} of sent` : undefined}
          onClick={() => openWorkflowsDrill('Connections Accepted', (w) => w.inWindow && w.connectionsAccepted > 0)} active={isOpen('workflows', 'Connections Accepted')} />
        <GxKpiCard label="Follow-ups Sent" icon={<ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />} iconColor={GX_COLORS.amber}
          value={v(data?.followUpsSent)} sub={data ? comparison(data.followUpsSent, prev?.followUpsSent) : undefined}
          onClick={() => openWorkflowsDrill('Follow-up Messages Sent', (w) => w.inWindow && w.followUpsSent > 0)} active={isOpen('workflows', 'Follow-up Messages Sent')} />
        <GxKpiCard label="Replies Received" icon={<ForumIcon sx={{ fontSize: 16 }} />} iconColor={GX_COLORS.pink}
          value={v(data?.repliesReceived)} sub={data ? `Reply rate ${fmtRate(data.replyPct)} of accepted` : undefined}
          onClick={() => openRepliesDrill('Replies Received', (r) => r.inWindow)} active={isOpen('replies', 'Replies Received')} />
      </Box>

      {/* Outreach Outcomes */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <ForumIcon sx={{ fontSize: 16, color: GX_COLORS.indigo }} />
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: GX_INK_700 }}>Outreach Outcomes</Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <OutcomeTile
          label="Conversations Logged" value={v(data?.conversationsLogged)}
          caption="One record per LinkedIn conversation (first reply, by reply date) — the same unified measure as Replies Received above. Click to see every conversation."
          icon={<ForumIcon sx={{ fontSize: 19 }} />} gradient="linear-gradient(135deg,#7C3AED,#C084FC)"
          onClick={() => openRepliesDrill('Conversations Logged', (r) => r.inWindow)} active={isOpen('replies', 'Conversations Logged')}
        />
        <OutcomeTile
          label="Meetings / SQLs" value={v(data?.meetingsSqlsBooked)}
          caption={`Meetings booked (lemlist campaign tally).${data?.meetingRatePct != null ? ` Meeting rate ${fmtRate(data.meetingRatePct)} of replies.` : ''} Kept alongside — not inside — the funnel, since it isn't a verified subset of Replies Received.`}
          icon={<EventAvailableIcon sx={{ fontSize: 19 }} />} gradient="linear-gradient(135deg,#0D9488,#10B981)"
        />
      </Box>

      <DrillPanel drill={drill} workflows={workflows} replies={replies} onClose={() => setDrill(null)} hub={data?.hubspot} />

      {/* Funnel — every stage clickable */}
      <GxSectionCard title="Outreach Funnel" icon={<SendIcon sx={{ fontSize: 18 }} />} iconColor={GX_COLORS.indigo}
        right={data?.window ? <GxPill color={GX_COLORS.indigo}>Selected: {shortDate(data.window.from)} – {shortDate(data.window.to)}</GxPill> : undefined}>
        {data?.funnel?.stages?.length ? (
          <>
            <Box sx={{ maxWidth: 672, mx: 'auto', pt: 1 }}>
              {data.funnel.stages.map((s) => (
                <Box key={s.key} component="button" onClick={() => onStageClick(s.key, s.label)}
                  sx={{
                    display: 'block', width: `${funnelWidthPct(funnelPct(s.key))}%`, mx: 'auto', p: 0, border: 'none', background: 'none',
                    cursor: 'pointer', transition: 'transform 200ms', '&:hover': { transform: 'scale(1.02)' },
                  }}>
                  <GxFunnelStage icon={FUNNEL_META[s.key]?.icon} label={s.label} value={gxInt(s.value)}
                    pct={funnelPct(s.key) != null ? Number(funnelPct(s.key)).toFixed(1) : '—'} gradient={FUNNEL_META[s.key]?.gradient} width={100} />
                </Box>
              ))}
            </Box>
            <Typography sx={{ mt: 1.5, fontSize: 12, color: GX_INK_500, textAlign: 'center', maxWidth: 576, mx: 'auto' }}>
              Funnel percentages are of Connections Sent. The KPI card&apos;s &quot;Reply rate&quot; is of Connections Accepted — a different denominator.
            </Typography>
          </>
        ) : (
          <Typography sx={{ fontSize: 13, color: GX_INK_500, textAlign: 'center' }}>No data yet — run a sync from Data → Connectors.</Typography>
        )}
      </GxSectionCard>

      {/* Sentiment + By-category */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' }, gap: 2, mb: 3 }}>
        <Paper variant="outlined" sx={{ ...GX_CARD_SX, p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SentimentSatisfiedIcon sx={{ fontSize: 17, color: GX_INK_700 }} />
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: GX_INK_700 }}>Reply Sentiment</Typography>
          </Box>
          <Box sx={{ position: 'relative', height: 288, mt: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={sentiment} dataKey="count" nameKey="sentiment" innerRadius="58%" outerRadius="88%" paddingAngle={2}
                  style={{ cursor: 'pointer' }}
                  onClick={(entry) => {
                    const s = entry?.sentiment ?? entry?.payload?.sentiment
                    if (s) openRepliesDrill(`Sentiment: ${s}`, (r) => r.inWindow && r.sentiment === s)
                  }}>
                  {sentiment.map((s, i) => <Cell key={s.sentiment} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />)}
                </Pie>
                <RTooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
            <Box sx={{ position: 'absolute', top: '44%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', pointerEvents: 'none' }}>
              <Typography sx={{ fontSize: 24, fontWeight: 700, color: GX_INK_900, lineHeight: 1.1 }}>{gxInt(data?.conversationsLogged ?? 0)}</Typography>
              <Typography sx={{ fontSize: 12, color: GX_INK_500 }}>conversations</Typography>
            </Box>
          </Box>
        </Paper>
        <Paper variant="outlined" sx={{ ...GX_CARD_SX, p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalOfferIcon sx={{ fontSize: 16, color: GX_INK_700 }} />
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: GX_INK_700 }}>By Target Category</Typography>
          </Box>
          <Box sx={{ height: 288, mt: 1.5 }}>
            {categoryChart.length === 0 && !isFetching ? (
              <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: GX_INK_500 }}>No workflows in this window.</Box>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={categoryChart} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef0f6" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10, fill: '#64748b' }} />
                  <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11, fill: '#334155' }} />
                  <RTooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(79,70,229,0.06)' }} />
                  <Bar dataKey="sent" name="Connections Sent" radius={[0, 4, 4, 0]} style={{ cursor: 'pointer' }}
                    onClick={(entry) => {
                      const name = entry?.name ?? entry?.payload?.name
                      if (name) openWorkflowsDrill(`Category: ${name}`, (w) => w.inWindow && w.category === name)
                    }}>
                    {categoryChart.map((c, i) => <Cell key={c.name} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Box>
        </Paper>
      </Box>

      {/* Reply pipeline status — the reference's Replies_Tracker stage; lemlist has none, so this
          only renders if the backend ever returns buckets. */}
      {data?.pipelineStatus?.length > 0 && (
        <Paper variant="outlined" sx={{ ...GX_CARD_SX, p: 2.5, mb: 3 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: GX_INK_700 }}>Reply Pipeline Status</Typography>
          <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {data.pipelineStatus.map((p) => (
              <Box key={p.status} component="button" onClick={() => openRepliesDrill(`Pipeline: ${p.status}`, (r) => r.inWindow && r.pipelineStatus === p.status)}
                sx={{ border: `1px solid ${GX_BORDER}`, borderRadius: '12px', px: 1.5, py: 1, textAlign: 'left', bgcolor: '#fff', cursor: 'pointer' }}>
                <Typography sx={{ fontSize: 18, fontWeight: 700, color: GX_INK_900 }}>{gxInt(p.count)}</Typography>
                <Typography sx={{ fontSize: 12, color: GX_INK_500 }}>{p.status}</Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {data && (
        <LeaderboardTable rows={data.leaderboard || []} total={data.leaderboardTotal} workflows={workflows} replies={replies} hub={data.hubspot} />
      )}

      <GxRequestCampaignSection />
    </Box>
  )
}
